import React, {
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';

import { FileRef, ResearchSheet as TResearchSheet } from 'types';
import {
  PageKey,
  ResearchSheetState,
  ResearchSheetType,
  SortOrder,
} from 'enums';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import {
  AdditionalRowComponentProps,
  TableColumn,
} from 'components/table/table-types';
import { Table } from 'components/table/table';
import { TableCellText } from 'components/table-cell/text-cell';
import { tableCellEnumFactory } from 'components/table-cell/enum-cell';
import { ReworkCtx } from 'components/rework-ctx/rework-ctx';
import { useTableHook } from 'components/table/table-hook';
import { I18nCtx } from 'components/i18n/i18n-context';
import { getItemsByLocale } from 'components/i18n/locale/utils';

import {
  fetchResearchSheets,
  grantRemoteAccess,
  useResearchSheetStates,
  widthdrawRemoteAccess,
} from './research-sheet-api';
import { PrimaryCellComponent } from './research-sheet-primary-cell';
import { ExtensionCellComponent } from './research-sheet-extension-cell';
import fetch from 'utils/fetch';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { ConfirmationRemoteAccessDialog } from 'modules/confirmation-checkbox/confirmation-remote-access-dialog';

const fileUrl = '/api/file';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      margin: theme.spacing(2),
    },
    wrapper: {
      backgroundColor: '#FAFAFA',
      minHeight: 100,
      padding: '0px 20px',
    },
    row: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
    link: {
      color: theme.palette.primary.main,
      cursor: 'pointer',
      textTransform: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    remoteAccessAction: {
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  })
);
type ClassesType = ReturnType<typeof useStyles>;

const headCells: TableColumn[] = [
  {
    id: 'ordinalNumber',
    label: 'Badatelský list AIS UK',
    CellComponent: TableCellText,
  },
  {
    id: 'expiration',
    label: 'Platný / Neplatný',
    CellComponent: ({ allData }) => {
      const row = allData as TResearchSheet;

      const approved = row.created
        ? new Date(row.created).toLocaleDateString('cs')
        : '';
      const expiration = row.expiration
        ? new Date(row.expiration).toLocaleDateString('cs')
        : '';
      return (
        <Typography variant="body1">{`${approved} - ${expiration}`}</Typography>
      );
    },
  },
  {
    id: 'state',
    label: 'Stav',
    CellComponent: tableCellEnumFactory(useResearchSheetStates, false),
  },
  {
    id: 'primary',
    label: 'Primární',
    CellComponent: PrimaryCellComponent,
  },
  {
    id: 'requestedExtensionTo',
    label: 'Žádost prodloužení platnosti',
    CellComponent: ExtensionCellComponent,
  },
];

async function refreshResearchSheet(
  id: string,
  setRow: React.Dispatch<SetStateAction<TResearchSheet>>
) {
  const response = await fetch(`/api/research-sheet/${id}`);

  const researchSheet = await response.json();

  setRow(researchSheet);
}

export function ResearchSheet() {
  const classes = useStyles();

  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const tableProps = useTableHook<TResearchSheet>({
    fetchFunction: fetchResearchSheets,
    params: {
      sort: [
        { field: 'primary', order: SortOrder.DESC },
        { field: 'state', order: SortOrder.ASC },
      ],
    },
  });

  useEffect(() => {
    setBreadcrumbs([
      ...initialBreadcrumbs.breadcrumbs,
      {
        title: 'Badatelské listy',
        key: PageKey.PORTFOLIO_RESEARCH_SHEET,
      },
    ]);
  }, [setBreadcrumbs]);

  return (
    <Grid container>
      <Grid item xs={12} className={classes.table}>
        <Table<TResearchSheet>
          {...tableProps}
          columns={headCells}
          expandableRows={true}
          AdditionalRowComponent={ResearchSheetDetail}
        />
      </Grid>
    </Grid>
  );
}

function ResearchSheetDetail({
  data,
}: AdditionalRowComponentProps<TResearchSheet>) {
  const classes = useStyles();
  const [researchSheetData, setResearchSheetData] = useState(data);

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <ResearchSheetDetailInfo
          classes={classes}
          researchSheet={researchSheetData}
          setResearchSheet={setResearchSheetData}
        />
      </Grid>
    </div>
  );
}

interface ResearchSheetDetailPartType {
  researchSheet: TResearchSheet;
  classes: ClassesType;
  setResearchSheet: React.Dispatch<SetStateAction<TResearchSheet>>;
}

function ResearchSheetDetailInfo({
  classes,
  researchSheet,
  setResearchSheet,
}: ResearchSheetDetailPartType) {
  const localeContext = useContext(I18nCtx);
  const {
    researchCategoryOptions,
    researchSheetTypeOptions,
  } = getItemsByLocale(localeContext.locale);

  const {
    researchCategories,
    researchTopic,
    type,
    inspectionRequest = [],
    inspectionAcknowledgement = [],
    remoteAccess,
    id,
  } = researchSheet;

  return (
    <>
      <Grid item xs={12} md={4}>
        <Typography variant="body2">Zaměření nahlížení</Typography>
        <Typography variant="body1">
          {researchCategories
            ?.map(
              (c) => researchCategoryOptions.find((rc) => rc.id === c)?.label
            )
            .filter((c) => !!c)
            .join(', ')}
        </Typography>
        <Typography variant="body2">Téma studia</Typography>
        <Typography variant="body1">{researchTopic}</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <ResearchSheetDetailState
          classes={classes}
          researchSheet={researchSheet}
        />
        <Typography variant="body2">Účel nahlížení</Typography>
        <Typography variant="body1">
          {researchSheetTypeOptions.find((rc) => rc.id === type)?.label}
        </Typography>
        {type === ResearchSheetType.CREDENTIAL && (
          <>
            <Typography variant="body2">Soubory</Typography>
            <Typography variant="body1">
              {!!inspectionRequest && (
                <ResearchSheetDocument
                  classes={classes}
                  text="Přílohy žádosti: "
                  documents={inspectionRequest}
                />
              )}
              {!!inspectionAcknowledgement && (
                <ResearchSheetDocument
                  classes={classes}
                  text="Potvrzení žádosti: "
                  documents={inspectionAcknowledgement}
                />
              )}
            </Typography>
          </>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <RemoteAccess
          researchSheetId={id}
          remoteAccess={!!remoteAccess}
          setResearchSheet={setResearchSheet}
        />
      </Grid>
    </>
  );
}

function ResearchSheetDocument({
  text,
  documents,
  classes,
}: {
  text: string;
  documents: FileRef[];
  classes: ClassesType;
}) {
  const handleDelete = (id: string) => {};

  return (
    <Typography variant="body1">
      <span>{text}</span>
      {documents.map(({ id, name }) => (
        <Chip
          label={name}
          onClick={() =>
            window.open(process.env.PUBLIC_URL + `${fileUrl}/${id}`, '_blank')
          }
          onDelete={() => handleDelete(id)}
        />
      ))}
    </Typography>
  );
}

function ResearchSheetDetailState({
  researchSheet,
  classes,
}: Omit<ResearchSheetDetailPartType, 'setResearchSheet'>) {
  const { handleResearchSheet } = useContext(ReworkCtx);
  const history = useHistory();

  const handleRework = () => {
    handleResearchSheet?.(researchSheet);
    history.push('/portfolio/badatelske-listy/oprava');
  };

  switch (researchSheet.state) {
    case ResearchSheetState.ACTIVE:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Aktivní badatelský list.
        </Typography>
      );
    case ResearchSheetState.SUBMITTED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Badatelský list je zadaný a čeká na posouzení správcem.
        </Typography>
      );
    case ResearchSheetState.REWORKED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Badatelský list je doplněný a čeká na posouzení správcem.
        </Typography>
      );
    case ResearchSheetState.CLOSED:
      return (
        <Typography style={{ color: '#4caf50' }}>
          Badatelský list je uzavřen.
        </Typography>
      );
    case ResearchSheetState.RETURNED:
      return (
        <Typography color="error">
          Badatelský list byl vrácen a je vyžadováno jeho přepracování dle
          poznámky správce:
          <br />
          <b>{researchSheet.adminNote}</b>
          <br />
          Pro doplnění &nbsp;
          <span className={classes.link} onClick={handleRework}>
            klikněte zde.
          </span>
        </Typography>
      );
    case ResearchSheetState.DENIED:
      return (
        <Typography color="error">
          Badatelského list byl zamítnut správcem s následujícím odůvodněním:
          <br />
          <b>{researchSheet.adminNote}</b>
        </Typography>
      );
    default:
      return <></>;
  }
}

function RemoteAccess({
  researchSheetId,
  remoteAccess,
  setResearchSheet,
}: {
  researchSheetId: string;
  remoteAccess: boolean;
  setResearchSheet: React.Dispatch<SetStateAction<TResearchSheet>>;
}) {
  const classes = useStyles();
  const { showNotification } = useContext(NotificationCtx);
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleConfirm = () => {
    if (!remoteAccess) {
      handleGrantRemoteAccess();
    }
    handleClose();
  };

  const handleGrantRemoteAccess = useCallback(async () => {
    const response = await grantRemoteAccess(researchSheetId);

    if (response.ok) {
      showNotification(
        `Souhlas s dálkovým přístupem byl úspěšne udělen.`,
        NotificationVariant.SUCCESS
      );
      await refreshResearchSheet(researchSheetId, setResearchSheet);
    } else {
      showNotification(
        `Při udělování souhlasu s dálkovým prohlížením nastala chyba:
        
        ${response.data?.message}`,
        NotificationVariant.ERROR
      );
    }
  }, [researchSheetId, setResearchSheet, showNotification]);

  const handleWithdrawRemoteAccess = useCallback(async () => {
    const response = await widthdrawRemoteAccess(researchSheetId);

    if (response.ok) {
      showNotification(
        `Souhlas s dálkovým přístupem byl úspěšne odvolán.`,
        NotificationVariant.SUCCESS
      );
      await refreshResearchSheet(researchSheetId, setResearchSheet);
    } else {
      showNotification(
        `Při udvolávání souhlasu s dálkovým prohlížením nastala chyba:
        
        ${response.data?.message}`,
        NotificationVariant.ERROR
      );
    }
  }, [researchSheetId, setResearchSheet, showNotification]);

  return (
    <>
      <ConfirmationRemoteAccessDialog
        open={open}
        handleConfirm={handleConfirm}
        handleClose={handleClose}
      />
      <Typography variant="body2">
        Souhlas s{' '}
        <span className={classes.link} onClick={handleOpen}>
          dálkovým prohlížením
        </span>
      </Typography>
      <Typography variant="body1" component="span">
        {`${remoteAccess ? 'Udělen' : 'Neudělen'}`}
      </Typography>
      {remoteAccess && (
        <>
          <Typography variant="body1" component="span">
            {' - '}
          </Typography>
          <Typography
            variant="body1"
            component="span"
            color="primary"
            onClick={handleWithdrawRemoteAccess}
            className={classes.remoteAccessAction}
          >
            Odvolat
          </Typography>
        </>
      )}
    </>
  );
}
